import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";

const features = [
  {
    index: "01",
    title: "One commercial thread",
    text: "Turn customer requests into offers, projects and invoices without copying data or losing the decisions in between.",
  },
  {
    index: "02",
    title: "Planning that reflects reality",
    text: "Schedule teams, record attendance and absences, and allocate worked time directly to the projects that carry the cost.",
  },
  {
    index: "03",
    title: "Inventory inside the workflow",
    text: "Quote catalog items, reserve stock and keep every receipt, issue and return tied to its commercial source.",
  },
];

const outcomes = [
  ["Requests", "Capture scope, dates, priorities and documents once."],
  ["Offers", "Price work with reusable catalog items and Swiss tax rules."],
  ["Projects", "Coordinate hierarchy, people, budgets, time and activity."],
  ["Invoices", "Create polished documents and follow payment status."],
];

const faqs = [
  {
    question: "Who is Offertli for?",
    answer:
      "Swiss service companies that manage customer requests, field or project work, employees, materials and invoicing—and want those workflows in one system.",
  },
  {
    question: "Can we run it on our own infrastructure?",
    answer:
      "Yes. Offertli is designed for self-hosting and ships as a container image with PostgreSQL-backed business data and file storage.",
  },
  {
    question: "Does it support Swiss commercial documents?",
    answer:
      "Yes. Offers and invoices support Swiss currencies, tax handling, QR-bill payment details and localized PDF and email output.",
  },
  {
    question: "Can we start with our existing process?",
    answer:
      "Yes. We map your roles, numbering, catalog and document settings first, then bring teams into the request-to-invoice workflow in stages.",
  },
];

function Mark() {
  return (
    <span className="mark" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

function App() {
  return (
    <div className="site-shell">
      <header className="nav wrap">
        <a className="brand" href="#top" aria-label="Offertli home">
          <Mark />
          <span>offertli</span>
        </a>
        <nav aria-label="Main navigation">
          <a href="#product">Product</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </nav>
        <a
          className="button button-small"
          href="mailto:mail@benavo.ch?subject=Offertli%20demo"
        >
          Book a conversation
        </a>
      </header>

      <main id="top">
        <section className="hero wrap">
          <div className="hero-copy">
            <p className="eyebrow">Built for Swiss service companies</p>
            <h1>
              From first request
              <br />
              to <em>paid invoice.</em>
            </h1>
            <p className="hero-lead">
              Offertli connects sales, projects, people, time, inventory and
              finance in one focused workspace. Less coordination overhead. More
              work moving forward.
            </p>
            <div className="hero-actions">
              <a
                className="button"
                href="mailto:mail@benavo.ch?subject=Offertli%20demo"
              >
                See Offertli in action
              </a>
              <a className="text-link" href="#product">
                Explore the workflow <span aria-hidden="true">→</span>
              </a>
            </div>
            <div className="trust-line">
              <span>Self-hosted</span>
              <span>Swiss QR-bill</span>
              <span>Four languages</span>
            </div>
          </div>

          <div
            className="product-frame"
            aria-label="Offertli project workspace preview"
          >
            <div className="frame-bar">
              <div className="frame-brand">
                <Mark /> offertli
              </div>
              <span>Project workspace</span>
            </div>
            <div className="app-preview">
              <aside>
                <span className="active">Overview</span>
                <span>Planning</span>
                <span>Commercial</span>
                <span>Time &amp; costs</span>
                <span>Inventory</span>
              </aside>
              <div className="preview-main">
                <div className="preview-title">
                  <div>
                    <small>PRJ-2026-0048</small>
                    <strong>Workshop refurbishment</strong>
                  </div>
                  <span className="status">Active</span>
                </div>
                <div className="metrics">
                  <div>
                    <small>Budget</small>
                    <strong>CHF 84'500</strong>
                  </div>
                  <div>
                    <small>Scheduled</small>
                    <strong>312 h</strong>
                  </div>
                  <div>
                    <small>Margin</small>
                    <strong>28.4%</strong>
                  </div>
                </div>
                <div className="timeline">
                  <div className="timeline-head">
                    <strong>This week</strong>
                    <span>27 Jul — 2 Aug</span>
                  </div>
                  <div className="person">
                    <span>AM</span>
                    <div>
                      <strong>Anna Meier</strong>
                      <i style={{ width: "74%" }} />
                    </div>
                  </div>
                  <div className="person">
                    <span>LS</span>
                    <div>
                      <strong>Luca Steiner</strong>
                      <i style={{ width: "56%" }} />
                    </div>
                  </div>
                  <div className="person">
                    <span>NK</span>
                    <div>
                      <strong>Noah Keller</strong>
                      <i style={{ width: "86%" }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="signal-strip" aria-label="Offertli workflow">
          <div className="wrap signal-grid">
            {outcomes.map(([title, text]) => (
              <div key={title}>
                <strong>{title}</strong>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="product wrap" id="product">
          <div className="section-intro">
            <p className="eyebrow">One source of operational truth</p>
            <h2>Every handoff stays connected.</h2>
            <p>
              The customer story, the project reality and the financial result
              stay linked—so the next person sees context, not another form.
            </p>
          </div>
          <div className="feature-grid">
            {features.map((feature) => (
              <article key={feature.index}>
                <span>{feature.index}</span>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="workflow-section">
          <div className="wrap workflow-grid">
            <div>
              <p className="eyebrow">Designed for the whole company</p>
              <h2>Calm for the office. Clear in the field.</h2>
              <p>
                Role-aware workspaces give each person the information and
                actions they need—without turning the product into a maze.
              </p>
              <ul>
                <li>Employee clocking, absences and project assignments</li>
                <li>Team planning with capacity and conflict visibility</li>
                <li>Live project cost, margin and inventory context</li>
                <li>Localized customer documents and communication</li>
              </ul>
            </div>
            <div className="workflow-card">
              <div className="flow-row">
                <span>01</span>
                <div>
                  <strong>Request arrives</strong>
                  <small>Scope, dates and attachments captured</small>
                </div>
              </div>
              <div className="flow-line" />
              <div className="flow-row">
                <span>02</span>
                <div>
                  <strong>Offer accepted</strong>
                  <small>Items, pricing and terms stay linked</small>
                </div>
              </div>
              <div className="flow-line" />
              <div className="flow-row">
                <span>03</span>
                <div>
                  <strong>Team delivers</strong>
                  <small>Plan, time, costs and stock in context</small>
                </div>
              </div>
              <div className="flow-line" />
              <div className="flow-row">
                <span>04</span>
                <div>
                  <strong>Invoice paid</strong>
                  <small>Commercial history remains complete</small>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pricing wrap" id="pricing">
          <div className="section-intro">
            <p className="eyebrow">Straightforward rollout</p>
            <h2>A system that fits the business you already run.</h2>
            <p>
              We configure Offertli around your company, migrate the essential
              data and help your team establish a clean operating rhythm.
            </p>
          </div>
          <div className="pricing-card">
            <div>
              <p className="price-label">Offertli for your company</p>
              <h3>Talk to us about your workflow.</h3>
              <p>
                Pricing reflects team size, rollout scope, hosting and support.
                You receive a clear proposal before implementation begins.
              </p>
            </div>
            <ul>
              <li>Guided setup and process mapping</li>
              <li>Self-hosted deployment options</li>
              <li>Data import and document configuration</li>
              <li>Direct support from the product team</li>
            </ul>
            <a
              className="button button-light"
              href="mailto:mail@benavo.ch?subject=Offertli%20pricing"
            >
              Request pricing
            </a>
          </div>
        </section>

        <section className="faq wrap" id="faq">
          <div className="section-intro compact">
            <p className="eyebrow">Questions, answered</p>
            <h2>Before we talk.</h2>
          </div>
          <div className="faq-list">
            {faqs.map((item) => (
              <details key={item.question}>
                <summary>
                  {item.question}
                  <span aria-hidden="true">+</span>
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="contact wrap">
          <div>
            <p className="eyebrow">A better operating rhythm starts here</p>
            <h2>Show us how work moves through your company.</h2>
          </div>
          <div>
            <p>
              We will walk through your current process and show where Offertli
              can remove handoffs, duplicate entry and missing context.
            </p>
            <a
              className="button"
              href="mailto:mail@benavo.ch?subject=Offertli%20conversation"
            >
              mail@benavo.ch
            </a>
          </div>
        </section>
      </main>

      <footer className="wrap footer">
        <a className="brand" href="#top">
          <Mark />
          <span>offertli</span>
        </a>
        <p>Operational software for Swiss service companies.</p>
        <a href="mailto:mail@benavo.ch">mail@benavo.ch</a>
      </footer>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
